import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Package, UserPlus } from "lucide-react";
import { api } from "../api";

/** Admin notifications bell. Polls /api/admin/notifications/unread-count
 *  every 30s; on click fetches the recent list. "Mark all read" clears
 *  everything, clicking an item marks only that one.
 */
export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try { const { data } = await api.get("/admin/notifications/unread-count"); setUnread(data?.unread ?? 0); }
    catch { /* silent */ }
  }, []);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/admin/notifications?limit=25"); setItems(data?.items || []); setUnread(data?.unread || 0); }
    catch { /* silent */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const i = setInterval(refreshCount, 30000);
    return () => clearInterval(i);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadList();
  };

  const markOne = async (n) => {
    try { await api.patch(`/admin/notifications/${n.id}`, { read: true }); } catch { /* silent */ }
    setItems((xs) => xs.map((x) => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    setUnread((u) => Math.max(0, u - (n.read_at ? 0 : 1)));
  };

  const markAll = async () => {
    try { await api.post("/admin/notifications/read-all"); } catch { /* silent */ }
    const stamp = new Date().toISOString();
    setItems((xs) => xs.map((x) => ({ ...x, read_at: x.read_at || stamp })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggle}
        aria-label="Notifications"
        data-testid="notifications-bell"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-md border border-[#E2E4E0] bg-white text-[#5C665A] hover:text-[#1A1D1A] hover:bg-[#F9F9F8] active:scale-95 transition"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-bold text-white bg-[#C94A4A] rounded-full"
            data-testid="notifications-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[480px] bg-white border border-[#E2E4E0] rounded-lg shadow-lg z-50 flex flex-col overflow-hidden"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E4E0]">
            <div className="db-h font-semibold text-sm">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-[#2D6A4F] hover:underline inline-flex items-center gap-1"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-[#5C665A]" data-testid="notifications-empty">
                No notifications yet. New orders and buyer registrations will appear here.
              </div>
            )}
            {items.map((n) => (
              <Link
                key={n.id}
                to={n.link || "#"}
                onClick={() => markOne(n)}
                data-testid={`notification-item-${n.id}`}
                className={`block px-3 py-2 border-t border-[#F2F2F0] first:border-t-0 hover:bg-[#F9F9F8] transition-colors ${!n.read_at ? "bg-[#F2F7F3]" : ""}`}
              >
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 w-7 h-7 rounded-full bg-[#E9F5EC] text-[#1B4332] grid place-items-center shrink-0">
                    {n.type === "new_order" ? <Package size={14} /> : <UserPlus size={14} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm leading-snug truncate ${!n.read_at ? "font-semibold text-[#1A1D1A]" : "text-[#1A1D1A]"}`}>
                      {n.title}
                    </div>
                    {n.body && <div className="text-xs text-[#5C665A] truncate">{n.body}</div>}
                    <div className="text-[10px] text-[#5C665A] mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read_at && <span className="w-2 h-2 bg-[#C94A4A] rounded-full shrink-0 mt-1.5"></span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
